import axios from 'axios'

import { authContext } from './adalAuth'
import { formatDatetimeLocal } from './utils'

import { LOGOUT, SESSION_TIMED_OUT } from '../actionTypes/auth'

/*
 * This function sets config settings for all axios calls in the project.
*/
export function axiosSetup(store) {
  axios.interceptors.request.use(config => {
    // keep track of headers as we add them
    let headers = {
      ...config.headers
    }

    // indicator that JWT token has expired (for lobbyists)
    let tokenHasExpired = false

    // if dev and internal then windows authentication is used
    if (process.env.REACT_APP_IS_DEV === 'true' && process.env.REACT_APP_IS_INTERNAL === 'true') {
      // this is used to make sure windows credentials are passed in correctly to work with windows authentication
      config.withCredentials = true
    } else {
      var authDataToken = ''
      // if it is an internal request then we must check if the user is valid in azure ad
      if (process.env.REACT_APP_USE_ADAL === 'true') {
        // check if a token has been retrieved from checking azure ad for the client id specified
        authDataToken = authContext.getCachedToken(process.env.REACT_APP_AZURE_CLIENT_ID)
      } else {
        // check if a token has been retrieved from checking the database and parse the json object stored
        let authData = sessionStorage.getItem('authorizationData')
        if (authData) {
          authData = JSON.parse(authData)
          authDataToken = authData.token

          // split JWT into header, payload, signature so we can check the expiry date in the payload
          let jwtParts = authDataToken.split('.')
          let payload = jwtParts[1]
          let decodedPayload = JSON.parse(window.atob(payload.replace('-', '+').replace('_', '/')))

          // expiry is in seconds so need to make it in seconds first
          let expiryDate = new Date(decodedPayload.exp * 1000)

          // if token hasn't expired yet then request a new token
          let timeLeftToExpiryInMinutes = Math.floor(((expiryDate - new Date()) / 1000 / 60))
          if (timeLeftToExpiryInMinutes > 0 && timeLeftToExpiryInMinutes < 15) {
            const tokenAxios = axios.create()

            // perform request to refresh auth token based on passed in token
            tokenAxios.post(`${process.env.REACT_APP_API_HOST}/api/user/refresh-auth-token`, null, {
              headers: {
                'Authorization': `Bearer ${authDataToken}`
              }
            })
              .then(response => {
                if (response && response.status === 200) {
                  sessionStorage.setItem('authorizationData', JSON.stringify({ token: response.data }))
                } else {
                  // remove token from storage
                  sessionStorage.removeItem('authorizationData')

                  // clear auth variables in store
                  store.dispatch({ type: LOGOUT })
                }
              })
          } else if (timeLeftToExpiryInMinutes <= 0) {
            // otherwise it has expired
            tokenHasExpired = true
          }
        }
      }

      // if there is a token then add to header to authorise request
      if (authDataToken) {
        headers = {
          ...headers,
          'Authorization': `Bearer ${authDataToken}`
        }
      }
    }

    config.headers = headers

    if (tokenHasExpired) {
      // remove token from storage
      sessionStorage.removeItem('authorizationData')

      // clear auth variables in store
      store.dispatch({ type: LOGOUT })

      // send message that session has expired
      let message = {
        data: {
          Success: false,
          ErrorMessages: {
            $values: ['Session has expired. Please login again.']
          }
        }
      }
      store.dispatch({ type: SESSION_TIMED_OUT, message: message })

      // stop request
      return false
    } else {
      if (config.data) {
        // axios calls JSON.stringify on the data object axios is using in the request
        // when JSON.stringify is called on a Date then it converts the Date into a datetime string into utc time.
        // i.e. using new Date returns something like this which contains the timezone Thu Aug 30 2018 18:36:30 GMT+0930 (Australian Central Standard Time)
        // JSON.stringify(new Date()) returns something like "2018-08-30T09:08:27.388Z" with the Z representing it is utc
        // when converting to utc then the timezone component is subtracted from the date i.e. Adelaide has - 9.5 hours or -10.5 hours depeneding on daylight saving
        // a consequence of this is that a date that doesn't have a time component i.e. one selected from a datepicker will be the previous day
        // this method loops through the data and converts all date objects into strings in local time instead of utc so they will be the correct date/time
        convertDatesToLocalDateTimeStrings(config.data)
      }

      return config
    }
  });

  axios.interceptors.response.use(null, (error) => {
    if (process.env.REACT_APP_USE_ADAL === 'true') {
      // if request comes back as unauthorised then try to get a new token in case the old one had timed out and try request again
      if (error.response && error.response.status === 401 && (typeof (error.config.unauthorisedCount) === 'undefined' || error.config.unauthorisedCount <= 1)) {
        authContext.logOut()
      } else {
        return Promise.reject(error)
      }
    }
  });
}

function convertDatesToLocalDateTimeStrings(data) {
  // loop through the proprtied of the data
  for (var propertyName in data) {
    // required so we don't through properties on the prototype
    if (data.hasOwnProperty(propertyName)) {
      // if there is a value and it is a Date then convert to string so that when stringify happens it is already a string so doesn't need converting then
      if (data[propertyName] && data[propertyName] instanceof Date) {
        data[propertyName] = formatDatetimeLocal(data[propertyName])
        // if there is a value and it is an object then call this method recursively with the property as the base in case there are any dates as a property of the current property
        // TODO - needs testing  
      } else if (data[propertyName] && typeof data[propertyName] === 'object') {
        convertDatesToLocalDateTimeStrings(data[propertyName])
        // TODO - needs testing
      } else if (data[propertyName] && data[propertyName] instanceof Array) {
        for (let i = 0; data[propertyName].length; i++) {
          convertDatesToLocalDateTimeStrings(data[propertyName][i])
        }
      }
    }
  }
}




// WEBPACK FOOTER //
// ./src/js/axios.js